'use client';

import type { ListDetail } from '@swishapp/api-client';
import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { useWishlistItems } from './hooks/use-wishlist-items';
import { useWishlistListsDetailed } from './hooks/use-wishlist-lists-detailed';
import { useWishlistMutations } from './hooks/use-wishlist-mutations';
import type { CreateItemInput } from './wishlist-proxy-client';

export interface WishlistContextValue {
  lists: ListDetail[] | null;
  loading: boolean;
  error: unknown;
  enabled: boolean;
  createList: (name: string) => void;
  deleteList: (listId: string) => void;
  addItem: (item: CreateItemInput) => void;
  removeItem: (itemId: string) => void;

  // Loading states for mutations
  isCreatingList: boolean;
  isDeletingList: boolean;
  isAddingItem: boolean;
  isRemovingItem: boolean;

  // Helper functions
  isInWishlist: (productId: string, variantId: string) => boolean;
}

export const WishlistContext = createContext<WishlistContextValue | null>(null);

interface WishlistProviderOptimizedProps {
  enabled?: boolean;
  children: ReactNode;
}

export function WishlistProviderOptimized({
  enabled = true,
  children,
}: WishlistProviderOptimizedProps) {
  // Use TanStack Query for data fetching
  const {
    data: lists,
    isLoading: listsLoading,
    isError: listsError,
    error: listsErrorValue,
  } = useWishlistListsDetailed();

  // Also fetch all items for more reliable isInWishlist checking
  const {
    data: allItems,
    isLoading: itemsLoading,
    isError: itemsError,
  } = useWishlistItems();

  // Combined loading state
  const loading = listsLoading || itemsLoading;
  const isError = listsError || itemsError;
  const error = listsError ? listsErrorValue : null;

  // Use mutations for data modification
  const {
    createList,
    deleteList,
    addItem,
    removeItem,
    isCreatingList,
    isDeletingList,
    isAddingItem,
    isRemovingItem,
  } = useWishlistMutations();

  // Helper function to check if an item is in the wishlist
  // Use the allItems data source (same as All Items view) for consistency
  const isInWishlist = useMemo(
    () => (productId: string, variantId: string) => {
      if (!allItems) return false;

      return allItems.some(
        (item) =>
          String(item.productId) === productId &&
          String(item.variantId) === variantId
      );
    },
    [allItems]
  );

  const value = useMemo<WishlistContextValue>(
    () => ({
      lists,
      loading,
      error: isError ? error : null,
      enabled,
      createList,
      deleteList,
      addItem,
      removeItem,
      isCreatingList,
      isDeletingList,
      isAddingItem,
      isRemovingItem,
      isInWishlist,
    }),
    [
      lists,
      loading,
      isError,
      error,
      enabled,
      createList,
      deleteList,
      addItem,
      removeItem,
      isCreatingList,
      isDeletingList,
      isAddingItem,
      isRemovingItem,
      isInWishlist,
    ]
  );

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx)
    throw new Error('useWishlist must be used within a WishlistProvider');
  return ctx;
}
